import React, { type PropsWithChildren } from 'react';
import { StyleSheet, View } from 'react-native';

import { useAppLayout } from '../../hooks/useAppLayout';
import { WebSidebar } from '../ui/glassTabBar/WebSidebar.web';
import { layout } from '../../core/theme/layout';
import { colors, radii } from '../../core/theme/tokens';
import { AppTopBar } from '../ui/AppTopBar';
import { useSampleData } from '../../contexts/SampleDataContext';
import { useWebTabLayout } from './WebTabLayoutContext';

/** Web dashboard — top bar + sidebar rail + main column (no overlap). */
export function WebDashboardShell({ children }: PropsWithChildren) {
  const { sidebar, sidebarWidth } = useAppLayout();
  const { tabBarProps } = useWebTabLayout();
  const { isSampleMode } = useSampleData();
  const topBar = isSampleMode ? null : <AppTopBar />;

  if (!sidebar) {
    return (
      <View style={styles.compactRoot}>
        {topBar}
        <View style={styles.compactMain}>{children}</View>
      </View>
    );
  }

  return (
    <View style={styles.root}>
      <View style={[styles.sidebarRail, { width: sidebarWidth }]}>
        {tabBarProps ? <WebSidebar {...tabBarProps} /> : null}
      </View>
      <View style={styles.mainColumn}>
        {topBar}
        <View style={styles.main}>{children}</View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  compactRoot: {
    flex: 1,
    minHeight: 0,
    width: '100%',
    maxWidth: layout.webAppMaxWidth,
    alignSelf: 'center',
    backgroundColor: colors.background,
  },
  compactMain: {
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  root: {
    flex: 1,
    flexDirection: 'row',
    width: '100%',
    maxWidth: layout.webDashboardMaxWidth,
    alignSelf: 'center',
    backgroundColor: colors.background,
    minHeight: 0,
    maxHeight: '100%',
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderColor: colors.glassBorder,
    borderRadius: radii.lg,
    marginVertical: 12,
    overflow: 'hidden',
    boxShadow: '0 24px 80px rgba(0, 0, 0, 0.45)',
  } as object,
  sidebarRail: {
    flexShrink: 0,
    alignSelf: 'stretch',
    minHeight: 0,
    backgroundColor: colors.background,
    borderRightWidth: StyleSheet.hairlineWidth,
    borderRightColor: colors.glassBorder,
  },
  mainColumn: {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
  },
  main: {
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
    backgroundColor: colors.background,
  },
});
